import { DialogActions } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { useEnvContext } from "context/EnvContext";
import {
  APP_CAREPROVIDER,
  FILE_TABLE_ACTION_UPLOAD,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  TRACK_EVENTS,
} from "core/consts";
import { PATHS } from "core/consts/pages";
import {
  ALLOWED_FILE_MIME_TYPES,
  MAX_FILE_SIZE_AHB_MB,
  MAX_FILE_SIZE_MB,
} from "core/model/files/config";
import { useFileUpload } from "ds_legacy/components/FileUploader";
import RSButton from "ds_legacy/components/RSButton";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import {
  APP_BACKGROUND,
  CUSTOM_BLACK,
  GREY_500,
} from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { dp } from "ds_legacy/materials/metrics";
import {
  Caption,
  FONT_SIZE_14,
  FONT_SIZE_18,
} from "ds_legacy/materials/typography";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { PaperclipIcon, UploadIcon, XIcon } from "lucide-react";
import { useMatch } from "react-router-dom";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";

export function FileUploadModalDragNDrop({
  closeModal,
  handleChange,
  isMessenger,
  isOpen,
  setAction,
}: {
  closeModal: () => void;
  handleChange: (file?: File) => void;
  isMessenger: boolean;
  isOpen: boolean;
  setAction: SetAction<FileFeatureData>;
}) {
  const { app } = useEnvContext();

  const isEligibility = useMatch(PATHS.APP.PATIENT_REHAB_FORMS_HOME);
  const translations = useTranslations();
  const { handleClick, inputRef, processedFile, reset } = useFileUpload();
  const { trackEvent } = useTracking();

  return (
    <Dialog
      key="dialog"
      fullWidth
      open={isOpen}
      sx={{ color: GREY_500 }}
      onClose={() => {
        trackEvent({
          cta: "outside_modal",
          file_implementation: "HIS",
          name: TRACK_EVENTS.UPLOAD_FILE_FROM_COMPUTER_CLOSED,
        });
        closeModal();
      }}
    >
      <VerticalLayout>
        <DialogTitle>
          <div>{translations.fileSection.dragAndDrop.title}</div>
          <IconButton
            aria-label={translations.actions.close}
            sx={{
              color: (theme) => theme.palette.grey[500],
              position: "absolute",
              right: 8,
              top: 8,
            }}
            onClick={() => {
              trackEvent({
                cta: "x",
                file_implementation: "HIS",
                name: TRACK_EVENTS.UPLOAD_FILE_FROM_COMPUTER_CLOSED,
              });
              closeModal();
            }}
          >
            <XIcon size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <VerticalLayout
            aligned
            justify="space-around"
            style={{
              backgroundColor: APP_BACKGROUND,
              height: dp(331),
              width: dp(550),
            }}
            onDragOver={(e: React.DragEvent) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e: React.DragEvent) => {
              trackEvent({
                file_implementation: "HIS",
                file_type: processedFile?.file.type,
                name: TRACK_EVENTS.UPLOAD_FILE_FROM_COMPUTER_DRAG_AND_DROP,
              });
              e.preventDefault();
              e.stopPropagation();
              handleChange(e.dataTransfer.files?.[0]);
              setAction({
                data: processedFile || ({} as FileFeatureData),
                type: FILE_TABLE_ACTION_UPLOAD,
              });
              if (!processedFile) {
                setAction({
                  data: {} as FileFeatureData,
                  type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
                });
              }
            }}
          >
            <VerticalLayout aligned style={{ color: GREY_500 }}>
              <UploadIcon size={24} style={{ color: GREY_500 }} />
              <Caption color={GREY_500} fontSize={FONT_SIZE_14}>
                {translations.fileSection.dragAndDrop.instructionDragAndDrop}
              </Caption>
            </VerticalLayout>
            <Caption color={GREY_500} fontSize={FONT_SIZE_18}>
              {
                translations.fileSection.dragAndDrop
                  .selectBetweenDragAndDropOptions
              }
            </Caption>
            <VerticalLayout aligned>
              <input
                ref={inputRef}
                accept={ALLOWED_FILE_MIME_TYPES.join(",")}
                hidden
                name="name"
                style={{
                  height: dp(331),
                  visibility: "hidden",
                  width: dp(514),
                }}
                type="file"
                onChange={(event) => handleChange(event.target.files?.[0])}
              />
              <RSButton
                color="white"
                id="select"
                LeftIcon={PaperclipIcon}
                loading="na"
                style={{ color: CUSTOM_BLACK, width: dp(200) }}
                variant="contained"
                onClick={() => {
                  trackEvent({
                    file_implementation: "HIS",
                    name: TRACK_EVENTS.UPLOAD_FILE_FROM_COMPUTER_SELECT_CLICKED,
                  });
                  handleClick();
                }}
              >
                {translations.actions.select}
              </RSButton>
              <VerticalLayout aligned style={{ marginTop: dp(10) }}>
                <Caption color={GREY_500} fontSize={FONT_SIZE_14}>
                  {translations.fileSection.dragAndDrop.maxFileSize({
                    maxSize: (isEligibility
                      ? MAX_FILE_SIZE_AHB_MB
                      : MAX_FILE_SIZE_MB
                    ).toFixed(1),
                  })}
                </Caption>
                <Caption color={GREY_500} fontSize={FONT_SIZE_14}>
                  {translations.fileSection.dragAndDrop.disclaimerFileType}
                </Caption>
              </VerticalLayout>
            </VerticalLayout>
          </VerticalLayout>
        </DialogContent>
        <DialogActions>
          <RSButton
            color="primary"
            id="cancel"
            loading="na"
            variant="text"
            onClick={() => {
              trackEvent({
                cta: "cancel",
                file_implementation: "HIS",
                name: TRACK_EVENTS.UPLOAD_FILE_FROM_COMPUTER_CLOSED,
              });
              if (app === APP_CAREPROVIDER) {
                setAction(undefined);
              } else {
                if (!isMessenger) {
                  closeModal();
                  return;
                }
                setAction({
                  data: {} as FileFeatureData,
                  type: FILE_TABLE_ACTION_UPLOAD_HIS,
                });
              }
              reset();
            }}
          >
            {translations.actions.cancel}
          </RSButton>
        </DialogActions>
      </VerticalLayout>
    </Dialog>
  );
}
